import "../EditFlightRoster.css";
import { TextField, Select, MenuItem, FormControl, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import DateLocalizationProvider from "../../../../../components/element/DateLocalizationProvider";
import SearchAircraftDialog from "../../../../../components/element/SearchAircraftDialog";
import { useFormData } from "../../../../../helpers/FormDataProvider";
import useAirlineAdminApi from "../../../../../services/api/useAirlineAdminApi";
import { DATE_FORMAT, TerminalType, TIME_FORMAT, REJECTION_REASONS_COMMERCIAL, AirportType } from "../../../../../utils/constants";
import { getAirportData, getFlightNumber, getFlightDelayTime } from "../../../../../utils/utils";
import "../EditFlightRoster.css";
import { useQuery } from "@tanstack/react-query";
import { TimeField } from "@mui/x-date-pickers";
import infoIcon from "../../../../../assets/images/info_icon.svg";

dayjs.extend(utc);

const EditFlightRosterTable = ({
  isEditing,
  airportData,
  flightPlanDetail,
  editedFlightDetails,
  setEditedFlightDetails,
}) => {
  const { fetchAircraftDetails } = useAirlineAdminApi();
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [showSearchAircraftDialog, setShowSearchAircraftDialog] =
    useState(false);
  const [selectedDepartureTerminal, setSelectedDepartureTerminal] =
    useState("");
  const [selectedArrivalTerminal, setSelectedArrivalTerminal] = useState("");
  const [arrivalTooltipOpen, setArrivalTooltipOpen] = useState(false);

  const { data: aircraftData } = useQuery({
    queryKey: ["get-aircraft-data", flightPlanDetail?.aircraftTypeId],
    queryFn: () => fetchAircraftDetails(flightPlanDetail?.aircraftTypeId),
    enabled: !!flightPlanDetail?.aircraftTypeId,
  });
  useEffect(() => {
    setSelectedAircraft(aircraftData);
  }, [aircraftData]);

  // Initialize terminal selections when flight plan details are available
  useEffect(() => {
    if (
      flightPlanDetail?.departureAirportId &&
      isDOMAirport(flightPlanDetail.departureAirportId)
    ) {
      // If there's already a terminal ID from the flight plan, use it
      if (flightPlanDetail?.departureAirportTerminalId) {
        setSelectedDepartureTerminal(flightPlanDetail.departureAirportTerminalId);
      } else {
        // No terminal selected - set to Main Terminal
        setSelectedDepartureTerminal("MAIN_TERMINAL");
      }
    }

    if (
      flightPlanDetail?.arrivalAirportId &&
      isDOMAirport(flightPlanDetail.arrivalAirportId)
    ) {
      // If there's already a terminal ID from the flight plan, use it
      if (flightPlanDetail?.arrivalAirportTerminalId) {
        setSelectedArrivalTerminal(flightPlanDetail.arrivalAirportTerminalId);
      } else {
        // No terminal selected - set to Main Terminal
        setSelectedArrivalTerminal("MAIN_TERMINAL");
      }
    }
  }, [flightPlanDetail, airportData]);

  const handleDelayTimeChange = (key) => (newValue) => {
    if (newValue) {
      const formattedTime = dayjs(newValue).format(TIME_FORMAT);
      setEditedFlightDetails({
        ...editedFlightDetails,
        [key]: formattedTime,
      });
    }
  };

  const handleInputChange = (key) => (event) => {
    setEditedFlightDetails({
      ...editedFlightDetails,
      [key]: event.target.value,
    });
  };

  const handleAircraftRegInputChange = (event) => {
    setEditedFlightDetails({
      ...editedFlightDetails,
      aircraftRegistrationNumber: event.target.value.toUpperCase(),
    });
  };

  const handleAircraftOnSelect = (aircraft) => {
    setSelectedAircraft(aircraft);
    setEditedFlightDetails({
      ...editedFlightDetails,
      aircraftTypeId: aircraft?.id,
    });
  };


  // Helper function to get airport data by ID (using common utils)
  const getAirportById = (airportId) => getAirportData(airportData, airportId);

  // Helper function to check if airport is DOM type
  const isDOMAirport = (airportId) => {
    const airport = getAirportById(airportId);
    return airport?.airportType === AirportType.DOMESTIC;
  };

  // Helper function to get terminal name by ID
  const getTerminalName = (airportId, terminalId) => {
    const airport = getAirportById(airportId);
    if (!airport || !airport.terminals) return "";
    const terminal = airport.terminals.find((t) => t.id === terminalId);
    return terminal?.name || "";
  };

  // Helper function to get rejection reason message
  const getRejectionReasonMessage = (reason) => {
    return REJECTION_REASONS_COMMERCIAL[reason] || reason;
  };

  // Handle terminal selection for departure
  const handleDepartureTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedDepartureTerminal(terminalId);

    // Only add terminal ID to editedFlightDetails if a specific terminal is selected
    if (terminalId && terminalId !== "" && terminalId !== "MAIN_TERMINAL") {
      setEditedFlightDetails({
        ...editedFlightDetails,
        departureAirportTerminalId: terminalId,
        removeDepartureTerminal: false,
      });
    } else if (terminalId === TerminalType.MAIN_TERMINAL) {
      // Main Terminal is selected - remove terminal and set flag if there was a previous terminal
      const { departureAirportTerminalId, ...rest } = editedFlightDetails;
      const hadPreviousTerminal = !!flightPlanDetail?.departureAirportTerminalId;
      setEditedFlightDetails({
        ...rest,
        removeDepartureTerminal: hadPreviousTerminal,
      });
    } else {
      // Empty selection - remove both terminal and flag
      const { departureAirportTerminalId, removeDepartureTerminal, ...rest } = editedFlightDetails;
      setEditedFlightDetails(rest);
    }
  };

  // Handle terminal selection for arrival
  const handleArrivalTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedArrivalTerminal(terminalId);

    // Only add terminal ID to editedFlightDetails if a specific terminal is selected
    if (terminalId && terminalId !== "" && terminalId !== "MAIN_TERMINAL") {
      setEditedFlightDetails({
        ...editedFlightDetails,
        arrivalAirportTerminalId: terminalId,
        removeArrivalTerminal: false,
      });
    } else if (terminalId === TerminalType.MAIN_TERMINAL) {
      // Main Terminal is selected - remove terminal and set flag if there was a previous terminal
      const { arrivalAirportTerminalId, ...rest } = editedFlightDetails;
      const hadPreviousTerminal = !!flightPlanDetail?.arrivalAirportTerminalId;
      setEditedFlightDetails({
        ...rest,
        removeArrivalTerminal: hadPreviousTerminal,
      });
    } else {
      // Empty selection - remove both terminal and flag
      const { arrivalAirportTerminalId, removeArrivalTerminal, ...rest } = editedFlightDetails;
      setEditedFlightDetails(rest);
    }
  };

  return (
    <div className="edit-flight-table-parent-container">
      <div className="edit-flight-table-container">
        <table className="edit-flight-table">
          <thead>
            <tr>
              <th className="width-90">Flight No.</th>
              <th className="width-90">Tail number</th>
              <th className="width-110">Aircraft Type</th>
              <th className="width-110">Engine Type</th>
              <th className="width-110">Engine Count</th>
              <th className="width-90">MTOW</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <p>{getFlightNumber(flightPlanDetail)}</p>
              </td>
              <td>
                {isEditing ? (
                  <TextField
                    id="flight-plan-tail-no"
                    variant="outlined"
                    className="edit-flight-textfield"
                    inputProps={{
                      autoComplete: "off",
                      maxLength: 15,
                    }}
                    style={{ width: "120px" }}
                    value={editedFlightDetails?.aircraftRegistrationNumber}
                    onChange={handleAircraftRegInputChange}
                  />
                ) : (
                  <p>
                    {flightPlanDetail?.aircraftRegistrationNumber
                      ? flightPlanDetail.aircraftRegistrationNumber.toUpperCase()
                      : "---"}
                  </p>
                )}
              </td>
              <td>
                {isEditing ? (
                  <div
                    className="edit-flight-plan-select-aircraft"
                    onClick={() => setShowSearchAircraftDialog(true)}
                  >
                    <p>{selectedAircraft?.icaoCode}</p>
                  </div>
                ) : (
                  <p>{aircraftData?.icaoCode ?? "---"}</p>
                )}
              </td>
              <td>
                {isEditing ? (
                  <p>{selectedAircraft?.engineType ?? "---"}</p>
                ) : (
                  <p>{aircraftData?.engineType ?? "---"}</p>
                )}
              </td>
              <td>
                {isEditing ? (
                  <p>{selectedAircraft?.engineCount ?? "---"}</p>
                ) : (
                  <p>{aircraftData?.engineCount ?? "---"}</p>
                )}
              </td>
              <td>
                {isEditing ? (
                  <p>{selectedAircraft?.maxTakeOffWeight ?? "---"}</p>
                ) : (
                  <p>{aircraftData?.maxTakeOffWeight ?? "---"}</p>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="edit-flight-table-container">
        <table className="edit-flight-table">
          <thead>
            <tr>
              <th>Origin</th>
              <th>Destination</th>
              <th className="width-200">Depart Date and Time</th>
              <th className="width-200">Arrival Date and Time</th>
              <th className="width-300">
                Departure From The Arrival Airport Date and Time
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="edit-flight-airport-terminal-container">
                  {isEditing &&
                    isDOMAirport(flightPlanDetail?.departureAirportId) ? (
                    <>
                      <p className="airport-name">
                        {
                          getAirportData(
                            airportData,
                            flightPlanDetail?.departureAirportId
                          )?.name
                        }
                      </p>
                      <FormControl
                        size="small"
                        className="edit-flight-terminal-dropdown-container"
                      >
                        <Select
                          value={selectedDepartureTerminal}
                          onChange={handleDepartureTerminalChange}
                          displayEmpty
                          variant="outlined"
                          className="edit-flight-terminal-dropdown"
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          <MenuItem value="MAIN_TERMINAL">
                            Main Terminal
                          </MenuItem>
                          {getAirportById(
                            flightPlanDetail?.departureAirportId
                          )?.terminals?.map((terminal) => (
                            <MenuItem key={terminal.id} value={terminal.id}>
                              {terminal.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  ) : (
                    <p
                      className="edit-flight-airport-name"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {
                        getAirportData(
                          airportData,
                          flightPlanDetail?.departureAirportId
                        )?.name
                      }
                      {isDOMAirport(flightPlanDetail?.departureAirportId) && (
                        <span className="edit-flight-terminal-text">
                          {getTerminalName(
                            flightPlanDetail?.departureAirportId,
                            flightPlanDetail?.departureAirportTerminalId
                          ) || "Main Terminal"}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </td>
              <td>
                <div className="edit-flight-airport-terminal-container">
                  {isEditing &&
                    isDOMAirport(flightPlanDetail?.arrivalAirportId) ? (
                    <>
                      <p className="edit-flight-airport-name">
                        {
                          getAirportData(
                            airportData,
                            flightPlanDetail?.arrivalAirportId
                          )?.name
                        }
                      </p>
                      <FormControl
                        size="small"
                        className="edit-flight-terminal-dropdown-container"
                      >
                        <Select
                          value={selectedArrivalTerminal}
                          onChange={handleArrivalTerminalChange}
                          displayEmpty
                          variant="outlined"
                          className="edit-flight-terminal-dropdown"
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          <MenuItem value="MAIN_TERMINAL">
                            Main Terminal
                          </MenuItem>
                          {getAirportById(
                            flightPlanDetail?.arrivalAirportId
                          )?.terminals?.map((terminal) => (
                            <MenuItem key={terminal.id} value={terminal.id}>
                              {terminal.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  ) : (
                    <p
                      className="edit-flight-airport-name"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {
                        getAirportData(
                          airportData,
                          flightPlanDetail?.arrivalAirportId
                        )?.name
                      }
                      {isDOMAirport(flightPlanDetail?.arrivalAirportId) && (
                        <span className="edit-flight-terminal-text">
                          {getTerminalName(
                            flightPlanDetail?.arrivalAirportId,
                            flightPlanDetail?.arrivalAirportTerminalId
                          ) || "Main Terminal"}
                          {flightPlanDetail?.fboFlightRejectionReason && (
                            <Tooltip
                              title={getRejectionReasonMessage(
                                flightPlanDetail?.fboFlightRejectionReason
                              )}
                              arrow
                              open={arrivalTooltipOpen}
                              onClose={() => setArrivalTooltipOpen(false)}
                              onClick={(e) => {
                                e.stopPropagation();
                                setArrivalTooltipOpen(!arrivalTooltipOpen);
                              }}
                              onTouchEnd={(e) => {
                                e.stopPropagation();
                                setArrivalTooltipOpen(!arrivalTooltipOpen);
                              }}
                            >
                              <img
                                src={infoIcon}
                                alt="info"
                                className="edit-flight-rejection-info-icon"
                              />
                            </Tooltip>
                          )}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </td>
              <td>
                <div>
                  <p>{`${dayjs(flightPlanDetail?.departureTime).format(
                    DATE_FORMAT
                  )} at ${dayjs(flightPlanDetail?.departureTime).format(
                    TIME_FORMAT
                  )}`}</p>
                  {flightPlanDetail?.departureDelay &&
                    flightPlanDetail?.departureDelay !== "00:00:00" ? (
                    <p className="edit-flight-delay-text">
                      {getFlightDelayTime(flightPlanDetail?.departureDelay, flightPlanDetail?.departureTime)}
                    </p>
                  ) : null}
                  {isEditing ? (
                    <DateLocalizationProvider>
                      <div className="edit-flight-depart-arrival-container">
                        <div>Delay by</div>
                        <TimeField
                          id="departure-delay-time-picker"
                          className="edit-flight-timePicker"
                          format={TIME_FORMAT}
                          onChange={handleDelayTimeChange("departureDelay")}
                        />
                      </div>
                    </DateLocalizationProvider>
                  ) : null}
                </div>
              </td>
              <td>
                <div>
                  <p>{`${dayjs(flightPlanDetail?.arrivalTime).format(
                    DATE_FORMAT
                  )} at ${dayjs(flightPlanDetail?.arrivalTime).format(
                    TIME_FORMAT
                  )}`}</p>
                  {flightPlanDetail?.arrivalDelay &&
                    flightPlanDetail?.arrivalDelay !== "00:00:00" ? (
                    <p className="edit-flight-delay-text">
                      {getFlightDelayTime(flightPlanDetail?.arrivalDelay, flightPlanDetail?.arrivalTime)}
                    </p>
                  ) : null}
                  {isEditing ? (
                    <DateLocalizationProvider>
                      <div className="edit-flight-depart-arrival-container">
                        <div>Delay by</div>
                        <TimeField
                          id="arrival-delay-time-picker"
                          className="edit-flight-timePicker"
                          format={TIME_FORMAT}
                          onChange={handleDelayTimeChange("arrivalDelay")}
                        />
                      </div>
                    </DateLocalizationProvider>
                  ) : null}
                </div>
              </td>
              <td>
                <p>
                  {flightPlanDetail?.departureTimeFromArrivalAirport
                    ? `${dayjs(
                      flightPlanDetail?.departureTimeFromArrivalAirport
                    ).format(DATE_FORMAT)} at ${dayjs(
                      flightPlanDetail?.departureTimeFromArrivalAirport
                    ).format(TIME_FORMAT)}`
                    : "---"}
                </p>
                {flightPlanDetail?.departureDelayFromArrivalAirport &&
                  flightPlanDetail?.departureDelayFromArrivalAirport !==
                  "00:00:00" ? (
                  <p className="edit-flight-delay-text">
                    {getFlightDelayTime(
                      flightPlanDetail?.departureDelayFromArrivalAirport,
                      flightPlanDetail?.departureTimeFromArrivalAirport
                    )}
                  </p>
                ) : null}
                {isEditing ? (
                  <DateLocalizationProvider>
                    <div className="edit-flight-depart-arrival-container">
                      <div>Delay by</div>
                      <TimeField
                        id="departure-delay-from-arrival-airport-time-picker"
                        className="edit-flight-timePicker"
                        format={TIME_FORMAT}
                        onChange={handleDelayTimeChange(
                          "departureDelayFromArrivalAirport"
                        )}
                      />
                    </div>
                  </DateLocalizationProvider>
                ) : null}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <SearchAircraftDialog
        open={showSearchAircraftDialog}
        onClose={() => setShowSearchAircraftDialog(false)}
        onSelectAircraft={handleAircraftOnSelect}
      />
    </div>
  );
};

export default EditFlightRosterTable;
